// Canonical CFL (Conflict / Disaster / Trade) intel taxonomy — v1.
// Plan: docs/designs/intel-domain-restructure.md (RESCOPE, task T13).
//
// One source of truth for:
//   * the SQL CHECK constraint values (mirrors `domain` enum in
//     infra/migrations/0040_intel_domain_v1.sql)
//   * the L4 classifier prompt output schema (lives in services/inference,
//     not here — this module is contract-only)
//   * the i18n catalog keys consumed by apps/terminal
//   * the design-token color names consumed by apps/terminal/src/styles/tokens.css
//
// Iron Rule #4: this module is pure types + small lookup maps. No LLM SDK,
// no I/O, no side effects. Safe to import from both server and browser code.

import { z } from 'zod';
import type { SignalType } from './intel.js';

// ---------------------------------------------------------------------------
// Top-level domain.
// ---------------------------------------------------------------------------

export const IntelDomain = z.enum(['conflict', 'disaster', 'trade']);
export type IntelDomain = z.infer<typeof IntelDomain>;

// ---------------------------------------------------------------------------
// Subcategory — canonical taxonomy. Adding a value is a contract change;
// classifier prompts and i18n catalogs must be updated in lockstep.
// ---------------------------------------------------------------------------

export const IntelSubcategory = z.enum([
  // Conflict
  'civil_unrest',
  'armed_conflict',
  'terrorism',
  'military_activity',
  'infrastructure_attack',
  'sanctions_export',
  'cyber_activity',
  // Disaster
  'earthquake',
  'flooding',
  'wildfire',
  'storm',
  'disease_outbreak',
  'humanitarian_crisis',
  // Trade
  'oil_tanker',
  'commercial_shipping',
  'port_congestion',
  'supply_chain_disruption',
]);
export type IntelSubcategory = z.infer<typeof IntelSubcategory>;

// ---------------------------------------------------------------------------
// Subcategory → parent domain. Used by:
//   * apps/terminal to pick the right color token for a signal pill
//   * services/inference to validate classifier output (subcategory must
//     belong to the claimed domain)
// ---------------------------------------------------------------------------

export const SUBCATEGORY_PARENT: Record<IntelSubcategory, IntelDomain> = {
  civil_unrest: 'conflict',
  armed_conflict: 'conflict',
  terrorism: 'conflict',
  military_activity: 'conflict',
  infrastructure_attack: 'conflict',
  sanctions_export: 'conflict',
  cyber_activity: 'conflict',
  earthquake: 'disaster',
  flooding: 'disaster',
  wildfire: 'disaster',
  storm: 'disaster',
  disease_outbreak: 'disaster',
  humanitarian_crisis: 'disaster',
  oil_tanker: 'trade',
  commercial_shipping: 'trade',
  port_congestion: 'trade',
  supply_chain_disruption: 'trade',
};

// ---------------------------------------------------------------------------
// i18n — apps/terminal looks these up via t('intelDomain.<key>') and
// t('intelSubcategory.<key>'). The locale JSON files in
// apps/terminal/src/locales/{en,fr,zh,he}.json carry the full register.
// ---------------------------------------------------------------------------

export const DOMAIN_I18N_PREFIX = 'intelDomain' as const;
export const SUBCATEGORY_I18N_PREFIX = 'intelSubcategory' as const;

// ---------------------------------------------------------------------------
// Design tokens — names defined in apps/terminal/src/styles/tokens.css.
// Reuses the existing severity color scale. Trade routes to the "low" hue
// because logistics signals are baseline-noise relative to conflict/disaster.
// ---------------------------------------------------------------------------

export const DOMAIN_COLOR_TOKEN: Record<IntelDomain, string> = {
  conflict: 'severity-high', // red-orange family
  disaster: 'severity-med', // amber family
  trade: 'severity-low', // cyan family (neutral for logistics)
};

// ---------------------------------------------------------------------------
// Wire-level signal_type (3-letter ETL codes) ←→ taxonomy bridge.
//
// Until the L4 sub-classifier backfills the historic ~1M rows, the canonical
// row column is the 3-letter `signal_type`. Aggregations come back keyed by
// those codes (SAN/CFL/SUP/SEI/WEA/OBL/POL/AVI/ANO). The hierarchical filter
// rail wants to render them under the (domain, subcategory) taxonomy.
//
// These maps are the bridge. SIGNAL_TYPE_TO_SUBCATEGORY is *partial* — only
// the codes that have an unambiguous 1:1 mapping today (single ETL source,
// single semantic meaning). The remaining codes either:
//   * span multiple subcategories (CFL is an ACLED umbrella covering
//     civil_unrest, armed_conflict, terrorism, military_activity, …), or
//   * have no v1 taxonomy slot (OBL=obligations/awards, POL=policy,
//     AVI=aviation tracks, ANO=anomaly detection).
// For both, we count at DOMAIN level only via SIGNAL_TYPE_TO_DOMAIN, and the
// frontend HIDES the un-mapped subcategory rows (see SUBCATEGORY_VISIBLE_IN_RAIL),
// surfacing their volume at the domain total instead.
// ---------------------------------------------------------------------------

export const SIGNAL_TYPE_TO_SUBCATEGORY: Partial<Record<SignalType, IntelSubcategory>> = {
  SAN: 'sanctions_export',
  SEI: 'earthquake',
  WEA: 'storm',
  FIR: 'wildfire',
  SUP: 'supply_chain_disruption',
  // CFL — multi-subcategory, omitted intentionally (see comment above).
  // OBL/POL/AVI/ANO/NEW — no v1 taxonomy slot.
};

/** Reverse of {@link SIGNAL_TYPE_TO_SUBCATEGORY}. Generated from the map so
 *  the two cannot drift. */
export const SUBCATEGORY_TO_SIGNAL_TYPE: Partial<Record<IntelSubcategory, SignalType>> =
  Object.freeze(
    Object.fromEntries(
      Object.entries(SIGNAL_TYPE_TO_SUBCATEGORY).map(([k, v]) => [v, k]),
    ) as Partial<Record<IntelSubcategory, SignalType>>,
  );

/** Every wire signal_type has a domain — even the ones with no clean
 *  subcategory. Used by the filter rail to render correct domain totals. */
export const SIGNAL_TYPE_TO_DOMAIN: Record<SignalType, IntelDomain | null> = {
  SAN: 'conflict',
  CFL: 'conflict',
  SUP: 'trade',
  OBL: null, // contract obligations / awards — outside conflict/disaster/trade
  POL: 'conflict', // policy / regulatory — closest fit; revisit when policy domain lands
  AVI: null, // aerospace tracks — Phase D layer, no taxonomy slot yet
  SEI: 'disaster',
  WEA: 'disaster',
  FIR: 'disaster', // wildfire / active fire
  ANO: null, // cross-domain anomaly — by definition spans multiple
  NEW: null, // news / media coverage — informational context, sits outside conflict/disaster/trade
};

/** Subcategories that have a 1:1 wire signal_type today. Drives
 *  SUBCATEGORY_VISIBLE_IN_RAIL — the frontend renders only these rows and
 *  hides taxonomy slots that have no backing data. */
export const SUBCATEGORY_HAS_SIGNAL: Record<IntelSubcategory, boolean> = (() => {
  const out = {} as Record<IntelSubcategory, boolean>;
  for (const s of IntelSubcategory.options) {
    out[s] = SUBCATEGORY_TO_SIGNAL_TYPE[s] !== undefined;
  }
  return out;
})();

// ---------------------------------------------------------------------------
// Filter-rail visibility — "collapse to real capability".
//
// A subcategory row is only RENDERED in the hierarchical rail when it can
// carry a true count today. Founder decision: do NOT show dead "—" rows for
// taxonomy slots that can never populate from the current wire data. The most
// important example is Conflict & Security: the CFL wire code (~135,800 rows)
// is a single ACLED umbrella covering five subcategories (civil_unrest,
// armed_conflict, terrorism, military_activity, infrastructure_attack). Until
// the L4 classifier splits CFL into those five, none of them can show a
// number — so we HIDE them and surface the real CFL total at the DOMAIN level
// (Conflict & Security: 135,800), via SIGNAL_TYPE_TO_DOMAIN.
//
// Visibility == "has a 1:1 wire code today". This is the *only* coherent
// treatment: a rendered subcat row always shows a real number; everything
// unbacked collapses up to its domain total. No half-dashes / half-zeros.
//
// TODO(intel-classifier): when services/etl/eidollon_etl/intel_classifier.py
// backfills the `signal_classification` table (0 rows today), each newly
// backed subcategory becomes a config flip here — add it to the visible set
// (or switch this map to read a per-subcategory `classified` flag) and the
// rail lights up the row automatically. No frontend rewrite required.
export const SUBCATEGORY_VISIBLE_IN_RAIL: Record<IntelSubcategory, boolean> =
  SUBCATEGORY_HAS_SIGNAL;
